import type { WindowablePage } from '@elmes/shared/plugin/manifest';
import { DM_ACTIVITY_ROUTE } from '../modules/pm/dmActivityUrl.js';
import { DM_DOCS_ROUTE } from '../modules/pm/dmDocsUrl.js';
import { DM_POLICIES_ROUTE } from '../modules/pm/dmPoliciesUrl.js';
import { joinCoreRoster, webExtensions } from './platformExtensions.js';

/**
 * A CORE-MODULE windowable page — a route-based platform surface (NOT a plugin
 * manifest row) that the Desktop Compositor launcher should list alongside the
 * manifest-driven windowable plugin pages. gh#1195.
 *
 * Why register these in code instead of the `plugins` table / a manifest?
 *   - A disk-manifest-less DB-only row is a permanent code↔DB DRIFT: nothing on
 *     disk owns it, so it can never be reconciled by the manifest sync.
 *   - `plugins` rows surface through `useModules` to EVERY consumer — the sidebar
 *     would then render a DUPLICATE PM entry next to the existing static one.
 *   - On the shared multi-env DB such a row would appear/disappear per env sync,
 *     making launcher visibility non-deterministic across dev/sandbox/release.
 * Keeping core windowable pages in code is the single source of truth for the
 * compositor launcher without polluting the module/sidebar registry.
 */
export interface CoreWindowablePage extends WindowablePage {
  /**
   * Minimum role required to see this entry in the launcher. `undefined` = visible
   * to any authenticated user — the launcher only mounts inside the authenticated
   * `/desktop` route, so an undefined gate mirrors the page's own route-level auth.
   */
  requiredRole?: string;
}

/**
 * Core-module windowable pages appended to the compositor launcher. They carry no
 * owning module `dashboardOrder`, so `useWindowablePages` treats them as order 999
 * and sorts them AFTER the manifest modules.
 *
 * ── THE DM ENTRIES POINT AT THE SHELL (gh#2607 PR-4) ───────────────────────
 * PR-3 left them on the pre-shell `/dm-*` addresses on purpose: Decision Inbox and Sprints
 * had no shell destination that matched their LABEL, so "open as page" reached the shell
 * through one redirect hop and `dmShellRoutes.test.ts` pinned that as transitional. PR-4
 * is the condition being discharged — every remaining entry names the address it means.
 *
 * PR-5 RETIRED `dm-sprints` AND `dm-inbox`, by operator decision. Both
 * had become launcher rows for readings that live INSIDE the shell — sprint membership is
 * the board's tree section, and the decision inbox is now the shell's `Inbox` tab — so a
 * window of their own was a second way in that could disagree with the first. What that
 * removal owed, and paid in the same change, is a REPLACEMENT for each thing the windows
 * were the only home of ([[feedback_retired_guard_deletion_needs_replacement_arm]]): the
 * inbox's `pm.question.answer` form moved to `/dm/inbox`, and the sprint panel's mirror
 * banner — K2's "observable staleness" — became the Overview tab's `Sprint sync` block.
 * Agent Runs, Project View and Memory Search stay.
 *
 * `pageRoute` may carry a QUERY here. It is what makes the label true: an entry whose
 * label names a reading but whose route lands on a bare board would be a launcher row
 * pointing at a surface the operator then has to go find. The route-parity guard composes
 * nested addresses and compares the PATH, so a query is a destination refinement rather
 * than a route this table invented.
 */
const BUILTIN_WINDOWABLE_PAGES: CoreWindowablePage[] = [
  {
    // PM (Project Management) core module — route `/pm`, sidebar icon `SquareKanban`
    // (Sidebar.tsx PM entry). The windowable desktop-window chrome is gh#1195; the
    // `/pm` route is authenticated-only, so requiredRole stays undefined (parity).
    pluginSlug: 'pm',
    pluginName: 'Project Management',
    pluginIcon: 'SquareKanban',
    pageKey: 'PmPage',
    pageRoute: '/pm',
    label: 'PM',
    windowable: {
      // Matches the UserViewWindow class's declared frame (`runtimeWindowClasses.ts`, which
      // is where the personal-view size now lives — gh#2517 replaced openUserViewWindow's
      // local DEFAULT_SIZE with it): a PM view window sizes the same whether it was opened
      // from a saved personal view or from the launcher. The two declarations are
      // independent, so the match is pinned by test rather than left as a comment.
      defaultSize: { width: 720, height: 520 },
      resizable: true,
      maximizable: true,
      minimizable: true,
      icon: 'SquareKanban',
    },
    requiredRole: undefined,
  },
  // `dm-sprints` and `dm-inbox` STOOD HERE and were removed by gh#2607 PR-5 (see the
  // table header for the decision and for what replaced each of them). Nothing else in
  // this file names them, and nothing should: a launcher entry is the ONE place a core
  // page's `pageKey` is offered to the compositor, so re-adding either means re-adding a
  // window, a `pageRegistry` entry and the argument for a second way into one reading.
  {
    // DM activity ledger (gh#2059 Faz 3 PR-9; renamed from the agent-run panel at DM W2
    // A1, when the flat list became the sprint ▸ session ▸ run tree) — the shell's
    // Activity tab. A SEPARATE entry
    // from the dm pages beside it, by the rule those pages already established: it
    // answers a different question under a DIFFERENT KEY (`pm.agent_run.read`),
    // and folding it into any of them would bind that page to a second key so a
    // caller holding only one would lose the half they are entitled to. That argument
    // is at its strongest here, because this key is BRAND NEW — nobody holds it until
    // the seed backfill runs, so "has the items, not the runs" is the ordinary
    // configuration for a while rather than an exotic one.
    //
    // requiredRole stays undefined. This page only READS and cannot write at all, so
    // there is no write to gate; a role-shaped launcher gate would be a second and
    // weaker gate on an axis (role) the server does not decide on (permission keys),
    // and the REST 403 is rendered in-panel.
    //
    // The dm entries in this launcher are this one, the project view host (K-G7-1 PR-2),
    // memory search (G7-C PR-3), policies (W3-B UIa) and docs (W3-B D3). The crowding note that stood
    // here — "grouping them under one dm heading is a launcher-wide UX decision" — is
    // discharged by PR-5's retirement of the sprint dashboard and the decision inbox
    // rather than by that grouping. NO COUNT: this comment carried one, it was wrong the moment two entries
    // left, and the names are what a reader can actually check against the table.
    //
    // ENGLISH on the DM-surface rule (operator, 2026-08-25): these strings are the
    // surface's NAME wherever the platform says it out loud — the launcher entry, the
    // window title, and any copy elsewhere that points an operator here
    // ([[feedback_no_turkish_for_established_terms]]).
    //
    // ── ALL FOUR COORDINATES MOVED TOGETHER (DM W2 A1) ──────────────────────
    // `pluginSlug`, `pageKey`, `pageRoute` and the two labels are ONE identity: a half
    // rename that kept the slug and moved the key is a launcher row whose window title
    // says one thing and whose component is another, and `core-page-routes-parity.test.ts`
    // asserts both coordinates for exactly that reason. The old `/dm/runs` address still
    // resolves — as a FORWARD, registered in App.tsx — so a bookmark lands on the tab
    // rather than on the catch-all.
    pluginSlug: 'dm-activity',
    pluginName: 'Activity',
    pluginIcon: 'Activity',
    pageKey: 'DmActivityTab',
    pageRoute: DM_ACTIVITY_ROUTE,
    label: 'Activity',
    windowable: {
      defaultSize: { width: 760, height: 560 },
      resizable: true,
      maximizable: true,
      minimizable: true,
      icon: 'Activity',
    },
    requiredRole: undefined,
  },
  {
    // DM project view host (gh#2059 K-G7-1 PR-2) — the shell's Board tab, the pane-stack
    // workspace over the platform project. A SEPARATE entry from the dm pages beside
    // it, by the rule they already established — but the argument lands DIFFERENTLY
    // here and that is worth stating, because this page brings no new key: it reads
    // `pm.item.read`, which its siblings hold too.
    //
    // What separates it is the QUESTION, not the key. This page answers "what am I
    // working on right now" as an addressable workspace — a set of open panes, an
    // inspected item and a chosen view mode, carried in the URL and shareable as a
    // link. It is also where "what does this project contain" is answered now: the
    // standalone `/dm-tree` page retired into this one's `?mode=tree`, which is a
    // FOLDING rather than a deletion — the containment walk kept its surface, its
    // re-anchor and its transition control, and gained the address the tree never had.
    // The rule that separation is about the question still holds: what folded in was a
    // second READING of one project, not a second question.
    //
    // requiredRole stays undefined, for the reason every dm sibling gives: this page
    // only READS, the REST gate is the one that decides, and a role-shaped launcher
    // gate would be a second and weaker gate on an axis the server does not decide on.
    // The 403 is rendered in-panel.
    //
    // `FolderOpen` is already in `ModuleIcon`'s ICON_MAP — an icon named here but
    // missing there resolves to the neutral `Box` and the launcher silently shows a
    // nameless square, which the sibling test asserts against.
    //
    // ENGLISH on the DM-surface rule, with its siblings. This entry is the one
    // that would have been strangest to leave behind: `DmProjectView` is the page the
    // directive started on, its `<h1>` already falls back to `Project View`, and the
    // window title sits directly above that heading.
    pluginSlug: 'dm-project',
    pluginName: 'Project View',
    pluginIcon: 'FolderOpen',
    pageKey: 'DmProjectView',
    pageRoute: '/dm/board',
    label: 'Project View',
    windowable: {
      // WIDER than its dm siblings' 760: this page carries a pane region AND a
      // fixed-width inspector column beside it, so the sibling default would open a
      // workspace whose panes start out narrower than a single item pane needs.
      defaultSize: { width: 980, height: 620 },
      resizable: true,
      maximizable: true,
      minimizable: true,
      icon: 'FolderOpen',
    },
    requiredRole: undefined,
  },
  {
    // DM memory search (SRS G7-C PR-3) — the shell's Memory tab, the operator's half of
    // `POST /api/dm/memory/search`. A SEPARATE entry from the dm pages beside it,
    // and here the separation argument is the QUESTION rather than the key: it shares
    // `pm.item.read` with `/dm/board` (a memory record IS a `pm_item` row, which is
    // why `DM_TOOL_PERMISSIONS` charges the whole memory family to that key), but "what
    // does this workspace remember about X" is a corpus question, not a containment walk
    // and not a workspace. Folding it into that page would put one page's chrome and
    // address in charge of two questions.
    //
    // requiredRole stays undefined, for the reason every dm sibling gives: this page only
    // READS, the REST gate is the one that decides, and a role-shaped launcher gate would
    // be a second and weaker gate on an axis the server does not decide on (permission
    // keys). The 403 is rendered in-panel.
    //
    // ── AND THE LAUNCHER GATE CANNOT MIRROR THE ONE GATE THAT IS NEW HERE ────
    // This door is also the first dm read with a per-principal BUDGET in front of it
    // (`memorySearchLimiter`, 30/min, ahead of the permission gate). That is deliberately
    // NOT mirrored as launcher visibility: a spent budget is a temporary state with a
    // repair measured in seconds, and hiding the entry over it would make the page
    // disappear from an operator who is using it correctly. The 429 is rendered in-panel
    // beside the form that hit it.
    //
    // `Brain` is already in `ModuleIcon`'s ICON_MAP — an icon named here but missing
    // there resolves to the neutral `Box` and the launcher silently shows a nameless
    // square, which the sibling tests assert against.
    //
    // ENGLISH on the DM-surface rule, with its dm siblings in CORE_WINDOWABLE_PAGES.
    pluginSlug: 'dm-memory',
    pluginName: 'Memory Search',
    pluginIcon: 'Brain',
    pageKey: 'DmMemorySearch',
    pageRoute: '/dm/memory',
    label: 'Memory Search',
    windowable: {
      // The dm siblings' 760×560. This page is a form over a single-column result list
      // — it carries neither `/dm/board`'s inspector column nor the tree's depth — so
      // there is nothing here to argue for departing from the shared default.
      defaultSize: { width: 760, height: 560 },
      resizable: true,
      maximizable: true,
      minimizable: true,
      icon: 'Brain',
    },
    requiredRole: undefined,
  },
  {
    // DM policies (W3-B UIa) — the shell's Policies tab as a launcher window. The REST
    // doors gate every read and write; no launcher-side role gate (the memory entry's rule).
    pluginSlug: 'dm-policies',
    pluginName: 'Policies',
    pluginIcon: 'ScrollText',
    pageKey: 'DmPoliciesTab',
    pageRoute: DM_POLICIES_ROUTE,
    label: 'Policies',
    windowable: {
      defaultSize: { width: 760, height: 560 },
      resizable: true,
      maximizable: true,
      minimizable: true,
      icon: 'ScrollText',
    },
    requiredRole: undefined,
  },
  {
    // DM docs (W3-B D3) — the `/dm/docs` page as a launcher window; not a shell tab (the
    // shell's help drawer links it). Read-only over the doc doors; no launcher-side role gate.
    pluginSlug: 'dm-docs',
    pluginName: 'Docs',
    pluginIcon: 'BookOpen',
    pageKey: 'DmDocsPage',
    pageRoute: DM_DOCS_ROUTE,
    label: 'Docs',
    windowable: {
      defaultSize: { width: 760, height: 560 },
      resizable: true,
      maximizable: true,
      minimizable: true,
      icon: 'BookOpen',
    },
    requiredRole: undefined,
  },
  {
    // Entity OS Console A1 (roadmap #474, epic #473) — admin operator console. The FIRST core
    // windowable page to carry a real `requiredRole` gate: the launcher lists it ONLY for admins
    // (canAccessRole → role >= admin). The API enforces the same fresh-admin boundary server-side;
    // this gate is the UI-visibility mirror (a non-admin never sees the entry).
    pluginSlug: 'console',
    pluginName: 'Entity OS Console',
    pluginIcon: 'Shield',
    pageKey: 'ConsolePage',
    pageRoute: '/console',
    label: 'Console',
    windowable: {
      defaultSize: { width: 900, height: 600 },
      resizable: true,
      maximizable: true,
      minimizable: true,
      icon: 'Shield',
    },
    requiredRole: 'admin',
  },
  {
    // Settings core module — windowable Settings surface (gh#1643, roadmap #490, PR-2). The
    // desktop-first counterpart to the /settings route. `pageRoute` reuses the EXISTING /settings
    // route (SettingsLayout stays UNCHANGED — kullanıcı kararı: mevcut route'lar davranışını korur),
    // which satisfies the route-parity guard; the window renders the self-contained SettingsPage via
    // `pageKey`. requiredRole is undefined: the window is visible to every authenticated user
    // (Güvenlik / Bağlantılar are user-facing) and the admin-only Yönetim/Geliştirici section
    // self-gates inside SettingsPage (mirroring the platform.settings API perm).
    pluginSlug: 'settings',
    pluginName: 'Ayarlar',
    pluginIcon: 'Settings',
    pageKey: 'SettingsPage',
    pageRoute: '/settings',
    label: 'Ayarlar',
    windowable: {
      defaultSize: { width: 820, height: 600 },
      resizable: true,
      maximizable: true,
      minimizable: true,
      icon: 'Settings',
    },
    requiredRole: undefined,
  },
];

/**
 * The launcher roster: the built-in entries above, then the build-time web slot's windowable pages
 * (route-based surfaces of an extension, same shape). A pageKey or pageRoute core owns refuses the build.
 */
export const CORE_WINDOWABLE_PAGES: CoreWindowablePage[] = joinCoreRoster(
  'windowable page',
  BUILTIN_WINDOWABLE_PAGES,
  webExtensions.windowablePages,
  (page) => [page.pageKey, page.pageRoute],
);
